<template>
  <div class="reuse-human-report">
    <!-- Main Container -->
    <div class="report-container">
      <!-- Simple Header -->
      <div class="report-header">
        <h2 class="report-title">Environmental Impact Analysis</h2>
        <p class="report-subtitle">Food Donation Impact Assessment</p>
      </div>

      <!-- Key Metrics Section -->
      <div class="metrics-section">
        <h4 class="metrics-title">Impact per Single Donation</h4>
        <div class="metrics-grid">
          <!-- Product Info -->
          <div class="metric-card">
            <div class="metric-icon">
              <i class="bi bi-box-seam"></i>
            </div>
            <div class="metric-label">Product</div>
            <div class="metric-value text-truncate" :title="surplusName">
              {{ surplusName }}
            </div>
            <div class="metric-detail">{{ baseSurplus }} kg {{ frequencyText }}</div>
          </div>

          <!-- CO₂eq Impact -->
          <div class="metric-card">
            <div class="metric-icon">
              <i class="bi bi-cloud"></i>
            </div>
            <div class="metric-label">CO<sub>2</sub><span class="no-transform">eq</span> Impact</div>
            <div class="metric-value" :class="{ 'positive': co2ImpactIsPositiveSingle, 'negative': !co2ImpactIsPositiveSingle }">
              {{ formatNumber(Math.abs(totalERForSingleDonation)) }} kg
            </div>
            <div class="metric-detail">
              {{ co2ImpactIsPositiveSingle ? 'Avoided' : 'Added' }}
            </div>
          </div>

          <!-- Water Impact -->
          <div class="metric-card">
            <div class="metric-icon">
              <i class="bi bi-droplet"></i>
            </div>
            <div class="metric-label">Water Impact</div>
            <div class="metric-value" :class="{ 'positive': totalWaterSavingsSingle >= 0, 'negative': totalWaterSavingsSingle < 0 }">
              {{ formatNumber(Math.abs(totalWaterSavingsSingle)) }} {{ WATER_UNIT_LABEL_EF31 }}
            </div>
            <div class="metric-detail">
              {{ totalWaterSavingsSingle >= 0 ? 'Saved' : 'Added' }}
            </div>
          </div>

          <!-- Land Impact -->
          <div class="metric-card">
            <div class="metric-icon">
              <i class="bi bi-tree"></i>
            </div>
            <div class="metric-label">Land Impact</div>
            <div class="metric-value" :class="{ 'positive': totalLandSavingsSingle >= 0, 'negative': totalLandSavingsSingle < 0 }">
              {{ formatNumber(Math.abs(totalLandSavingsSingle)) }} {{ LAND_UNIT_LABEL_EF31 }}
            </div>
            <div class="metric-detail">
              {{ totalLandSavingsSingle >= 0 ? 'Saved' : 'Added' }}
            </div>
          </div>
        </div>
      </div>

      <!-- Understanding Environmental Indicators -->
      <div class="content-section">
        <h3 class="section-title">
          <i class="bi bi-info-circle"></i>
          Understanding Environmental Indicators
        </h3>

        <div class="indicators-explanation">
          <!-- CLIMATE -->
          <div class="indicator-explanation-card">
            <div class="indicator-exp-header">
              <i class="bi bi-cloud indicator-exp-icon"></i>
              <h4>Climate Impact</h4>
            </div>
            <ul style="margin: 0 0 0.75rem 1.0rem;">
              <li><strong>Unit:</strong> kg CO₂-equivalent (CO₂eq)</li>
              <li><strong>What it measures:</strong> The total warming effect from all greenhouse gases (CO₂, methane, etc.) released during farming, processing, packaging, and transport.</li>
              <li><strong>How to interpret:</strong> All gases are converted into their CO₂-equivalent impact. One kilogram of CO₂eq has the same warming effect as one kilogram of pure CO₂.</li>
            </ul>
            <p style="margin:0;color:#666;">
              <em>In simple terms:</em> This is the "climate footprint" of food. Lower values mean less contribution to global warming.
            </p>
          </div>

          <!-- WATER -->
          <div class="indicator-explanation-card">
            <div class="indicator-exp-header">
              <i class="bi bi-droplet indicator-exp-icon"></i>
              <h4>Water Scarcity</h4>
            </div>
            <ul style="margin: 0 0 0.75rem 1.0rem;">
              <li><strong>Unit:</strong> m³ <em>world eq</em> (world-equivalent cubic meters)</li>
              <li><strong>What it measures:</strong> Water consumption weighted by how scarce water is in the specific region where it's used.</li>
              <li><strong>How to interpret:</strong> This is not raw liters. Using 1 liter in a water-scarce desert has a much higher value than using 1 liter in a water-rich region. The unit accounts for this regional difference.</li>
            </ul>
            <p style="margin:0;color:#666;">
              <em>In simple terms:</em> 1 m³ world eq represents one cubic meter of water used under average global scarcity conditions.
            </p>
          </div>

          <!-- LAND -->
          <div class="indicator-explanation-card">
            <div class="indicator-exp-header">
              <i class="bi bi-tree indicator-exp-icon"></i>
              <h4>Land Use</h4>
            </div>
            <ul style="margin: 0 0 0.75rem 1.0rem;">
              <li><strong>Unit:</strong> <em>point</em> (a dimensionless soil-quality score)</li>
              <li><strong>What it measures:</strong> How much agricultural use degrades soil functions (productivity, erosion protection, water filtration, biodiversity) compared to the soil's natural state.</li>
              <li><strong>How to interpret:</strong> Points represent quality degradation, not just area. Using 1 m² of highly sensitive soil generates more points than using 1 m² of less sensitive soil.</li>
            </ul>
            <p style="margin:0;color:#666;">
              <em>In simple terms:</em> Higher points mean greater pressure on soil ecosystems. This indicator measures quality loss, not just the size of land used.
            </p>
          </div>
        </div>
      </div>

      <!-- Methodology Section -->
      <div class="content-section">
        <h3 class="section-title">
          <i class="bi bi-info-circle"></i>
          How We Calculate Your Environmental Impact
        </h3>
        <div class="methodology-content">
          <div class="methodology-explanation">
            <h4>What happens when you donate food?</h4>
            <p>
              When your company donates surplus food instead of throwing it away, three important things happen
              that affect multiple environmental indicators:
            </p>

            <div class="impact-explanation">
              <div class="impact-item">
                <div class="impact-number">1</div>
                <div class="impact-text">
                  <h5>Less new food needs to be produced</h5>
                  <p>
                    When people receive your donated food, they buy less from stores. Research shows that for every
                    <strong>3 kilograms</strong> of food you donate, people buy about <strong>1 kilogram less</strong>
                    of new food (typically basic staples like bread). This means:
                  </p>
                  <ul style="margin-top: 0.5rem; margin-bottom: 0; padding-left: 1.5rem;">
                    <li>Lower <strong>climate impact</strong> from avoided farming, processing, and packaging emissions</li>
                    <li>Reduced <strong>water impact</strong> (scarcity-weighted) from irrigation and production</li>
                    <li>Less <strong>land degradation</strong> and agricultural ecosystem pressure</li>
                  </ul>
                </div>
              </div>

              <div class="impact-item">
                <div class="impact-number">2</div>
                <div class="impact-text">
                  <h5>Less waste goes to treatment facilities</h5>
                  <p>
                    Studies show that <strong>78% of donated food actually gets eaten</strong>, which means it doesn't
                    end up in waste treatment plants or landfills. This reduction in waste treatment impacts:
                  </p>
                  <ul style="margin-top: 0.5rem; margin-bottom: 0; padding-left: 1.5rem;">
                    <li><strong>Climate:</strong> Avoided emissions from decomposition and waste processing</li>
                    <li><strong>Water:</strong> Reduced water impact from waste treatment infrastructure and processes</li>
                    <li><strong>Land:</strong> Reduced environmental degradation from landfill use and waste facilities</li>
                  </ul>
                </div>
              </div>

              <div class="impact-item">
                <div class="impact-number">3</div>
                <div class="impact-text">
                  <h5>Transportation has environmental impacts</h5>
                  <p>
                    Getting your surplus food to donation centers requires transportation over the <strong>{{ formatDistance(distanceKm) }} km</strong>
                    distance you've specified. Our calculations include transport impacts across all indicators using EF v3.1 values:
                  </p>
                  <ul style="margin-top: 0.5rem; margin-bottom: 0; padding-left: 1.5rem;">
                    <li><strong>Climate Change:</strong> 1.58 kg CO<sub>2</sub> eq per tonne-kilometer (truck emissions)</li>
                    <li><strong>Water Use:</strong> 0.163 m<sup>3</sup> world eq per tonne-kilometer (fuel production & vehicle lifecycle)</li>
                    <li><strong>Land Use:</strong> 9.29 points per tonne-kilometer (infrastructure & fuel impacts)</li>
                  </ul>
                </div>
              </div>
            </div>

            <div class="methodology-summary">
              <h4>Your Net Environmental Impact</h4>
              <p>
                We combine all three factors above to calculate your total environmental footprint across all indicators.
                The same methodology applies to <strong>CO<sub>2</sub><span class="no-transform">eq</span></strong>,
                <strong>Water Use</strong>, and <strong>Land Use</strong> - each using specific environmental intensity
                values from our product database. If the benefits from reduced production and waste treatment outweigh
                any offsets, you achieve a <strong class="positive">positive environmental impact</strong> for that indicator.
              </p>

              <div class="calculation-note">
                <i class="bi bi-exclamation-circle-fill"></i>
                <p>
                  <strong>Limitations: Note, IT IS NOT A COMPREHENSIVE LIFE CYCLE ASSESSMENT (LCA).</strong> We would like to emphasise that this tool does not perform comprehensive LCA calculations. It is obvious that a tool such as this can only work with average values. The limitations include the breadth of products and the incomplete data provided by users. The results from foodcycle.ai are intended to give users an overview and motivate them to delve deeper into the topic of environmental impact in their area of responsibility. Specific, reliable and comparable environmental impacts can only be calculated on the basis of very specifically defined framework conditions. The LCA method provides for a scoping step for this purpose.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Visualization Section with Period and Indicator Selection -->
      <div class="content-section">
        <!-- Section Header with Period Selector -->
        <div class="impact-header">
          <h3 class="section-title">
            <i class="bi bi-bar-chart"></i>
            Impact Breakdown
          </h3>
          <div class="period-selector-inline">
            <span class="period-label">View by:</span>
            <div class="period-buttons">
              <button
                v-for="period in periodOptions"
                :key="period"
                class="selector-btn"
                :class="{ 'active': selectedPeriod === period }"
                :disabled="disablePeriodButton(period)"
                @click="selectedPeriod = period"
              >
                {{ periodLabel(period) }}
              </button>
            </div>
          </div>
        </div>
        
        <div v-if="stepperStore.wasteInfo.seasonal" class="seasonal-note">
          <i class="bi bi-exclamation-triangle"></i>
          Note: This is a seasonal product. Calculations are estimates.
        </div>

        <!-- Unified Impact Analysis Card -->
        <div class="unified-impact-card">
          <!-- Card Header with Indicator Tabs -->
          <div class="card-header-tabs">
            <h5 class="card-main-title">Environmental Savings Analysis</h5>
            <div class="indicator-tabs">
              <button
                v-for="indicator in indicatorOptions"
                :key="indicator.key"
                class="indicator-tab"
                :class="{ 'active': selectedIndicator === indicator.key }"
                @click="selectedIndicator = indicator.key"
              >
                <i :class="indicator.icon"></i>
                <span>{{ indicator.label }}</span>
              </button>
            </div>
          </div>

          <!-- Card Body with Two Columns -->
          <div class="card-body-grid">
            <!-- Left Side: Understanding -->
            <div class="understanding-panel">
              <h6 class="panel-title">Understanding Your Savings</h6>
              <p class="panel-intro">
                How {{ selectedIndicatorName }} is saved through your donations:
              </p>
              
              <div class="savings-breakdown">
                <div class="breakdown-item">
                  <div class="breakdown-indicator" :style="{ background: palette.production }"></div>
                  <div class="breakdown-info">
                    <strong>Production Avoided</strong>
                    <span>Resources saved when donated food replaces new production</span>
                  </div>
                </div>

                <div class="breakdown-item">
                  <div class="breakdown-indicator" :style="{ background: palette.waste }"></div>
                  <div class="breakdown-info">
                    <strong>Waste Treatment Saved</strong>
                    <span>{{ Math.round(FWDE * 100) }}% of food consumed instead of waste</span>
                  </div>
                </div>

                <div class="breakdown-item">
                  <div class="breakdown-indicator" :style="{ background: palette.transport }"></div>
                  <div class="breakdown-info">
                    <strong>Transport Offset</strong>
                    <span>Offsets savings due to {{ formatDistance(distanceKm) }} km delivery</span>
                  </div>
                </div>

                <div class="breakdown-item total">
                  <div class="breakdown-indicator" :style="totalIndicatorStyle"></div>
                  <div class="breakdown-info">
                    <strong :class="totalLegendClass">Total Net Savings</strong>
                    <span>{{ totalLegendDescription }}</span>
                  </div>
                </div>
              </div>

              <div class="panel-note">
                <i class="bi bi-lightbulb"></i>
                <span>Taller bars in the chart = greater environmental benefits</span>
              </div>
            </div>

            <!-- Divider Line -->
            <div class="vertical-divider"></div>

            <!-- Right Side: Chart -->
            <div class="chart-panel">
              <div class="chart-panel-header">
                <h6 class="chart-title">{{ periodLabel(selectedPeriod) }} Impact</h6>
                <span class="chart-indicator">{{ indicatorOptions.find(opt => opt.key === selectedIndicator).label }}</span>
              </div>
              <div ref="impactChart" class="chart-area-unified"></div>
            </div>
          </div>
        </div>

        <!-- Data Table -->
        <div class="data-table-container">
          <div class="table-period-header">
            <span class="period-badge">
              <i class="bi bi-calendar3"></i>
              {{ periodLabel(selectedPeriod) }} Values
            </span>
          </div>
          <table class="data-table">
            <thead>
              <tr>
                <th>Category</th>
                <th class="text-center">
                  <div class="header-content">
                    <i class="bi bi-cloud"></i>
                    <div>
                      <div>CO<sub>2</sub><span class="no-transform">eq</span></div>
                      <div class="unit-label">kg {{ tablePeriodDescriptor }}</div>
                    </div>
                  </div>
                </th>
                <th class="text-center">
                  <div class="header-content">
                    <i class="bi bi-droplet"></i>
                    <div>
                      <div>Water Use</div>
                      <div class="unit-label">{{ WATER_UNIT_LABEL_EF31 }} {{ tablePeriodDescriptor }}</div>
                    </div>
                  </div>
                </th>
                <th class="text-center">
                  <div class="header-content">
                    <i class="bi bi-tree"></i>
                    <div>
                      <div>Land Use</div>
                      <div class="unit-label">{{ LAND_UNIT_LABEL_EF31 }} {{ tablePeriodDescriptor }}</div>
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <div class="category-label">
                    <span class="category-name">
                      <i class="bi bi-factory text-muted"></i> Production Effect
                    </span>
                    <span class="category-desc">Reduced need for new food</span>
                  </div>
                </td>
                <td class="text-center"><span class="table-value value-production">{{ formatNumber(productionSavedForSelectedPeriod) }}</span></td>
                <td class="text-center"><span class="table-value value-production">{{ formatNumber(waterProductionEffect) }}</span></td>
                <td class="text-center"><span class="table-value value-production">{{ formatNumber(landProductionEffect) }}</span></td>
              </tr>
              <tr>
                <td>
                  <div class="category-label">
                    <span class="category-name">
                      <i class="bi bi-trash text-muted"></i> Waste Avoided
                    </span>
                    <span class="category-desc">Not sent to treatment</span>
                  </div>
                </td>
                <td class="text-center"><span class="table-value value-waste">{{ formatNumber(wasteSavedForSelectedPeriod) }}</span></td>
                <td class="text-center"><span class="table-value value-waste">{{ formatNumber(waterWasteSaved) }}</span></td>
                <td class="text-center"><span class="table-value value-waste">{{ formatNumber(landWasteSaved) }}</span></td>
              </tr>
              <tr>
                <td>
                  <div class="category-label">
                    <span class="category-name">
                      <i class="bi bi-truck text-muted"></i> Transport
                    </span>
                    <span class="category-desc">Delivery emissions</span>
                  </div>
                </td>
                <td class="text-center"><span class="table-value value-transport">{{ formatNumber(transportEmissionsForSelectedPeriod) }}</span></td>
                <td class="text-center"><span class="table-value value-transport">{{ formatNumber(waterTransportAdded) }}</span></td>
                <td class="text-center"><span class="table-value value-transport">{{ formatNumber(landTransportAdded) }}</span></td>
              </tr>
              <tr class="total-row">
                <td>
                  <div class="category-label">
                    <span class="category-name">
                      <i class="bi bi-calculator"></i>
                      <strong>Net Effect</strong>
                    </span>
                  </div>
                </td>
                <td class="text-center">
                  <strong :class="totalValueClass('co2')">
                    {{ formatNumber(totalERForSelectedPeriod) }}
                  </strong>
                </td>
                <td class="text-center">
                  <strong :class="totalValueClass('water')">{{ formatNumber(totalWaterSavings) }}</strong>
                </td>
                <td class="text-center">
                  <strong :class="totalValueClass('land')">{{ formatNumber(totalLandSavings) }}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Insights Section -->
      <div class="content-section">
        <h3 class="section-title">
          <i class="bi bi-lightbulb"></i>
          What This Means For You
        </h3>

        <!-- Indicators Grid -->
        <div class="indicators-impact-grid">
          <!-- Climate Change Card -->
          <div class="indicator-impact-card" v-if="totalERForSelectedPeriod >= 0">
            <div class="indicator-impact-header">
              <h4><i class="bi bi-cloud indicator-exp-icon"></i> Climate Change</h4>
              <p class="indicator-value">{{ formatNumber(Math.abs(totalERForSelectedPeriod)) }} kg CO<sub>2</sub> eq avoided</p>
              <p class="indicator-note">Your donations prevent this much greenhouse gas from being released.</p>
            </div>

            <div class="indicator-impact-body" v-if="climateComparisonsAll">
              <p class="comparison-label">To put this in perspective, this equals the climate impact of producing:</p>
              <div class="comparisons-table">
                <div
                  v-for="cmp in climateComparisonsAll"
                  :key="'climate-'+cmp.key"
                  class="comparison-row"
                >
                  <span class="comparison-icon">{{ cmp.emoji }}</span>
                  <span class="comparison-amount">{{ formatNumber(cmp.equivalentKg) }} kg</span>
                  <span class="comparison-product">{{ cmp.name }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Water Scarcity Card -->
          <div class="indicator-impact-card">
            <div class="indicator-impact-header">
              <h4><i class="bi bi-droplet indicator-exp-icon"></i> Water Scarcity</h4>
              <p class="indicator-value">{{ formatNumber(Math.abs(totalWaterSavings)) }} {{ WATER_UNIT_LABEL_EF31 }} saved</p>
              <p class="indicator-note">Your donations reduce pressure on water resources in water-scarce regions.</p>
            </div>

            <div class="indicator-impact-body" v-if="waterComparisonsAll">
              <p class="comparison-label">To put this in perspective, this equals the water impact of producing:</p>
              <div class="comparisons-table">
                <div
                  v-for="cmp in waterComparisonsAll"
                  :key="'water-'+cmp.key"
                  class="comparison-row"
                >
                  <span class="comparison-icon">{{ cmp.emoji }}</span>
                  <span class="comparison-amount">{{ formatNumber(cmp.equivalentKg) }} kg</span>
                  <span class="comparison-product">{{ cmp.name }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Land Use Card -->
          <div class="indicator-impact-card">
            <div class="indicator-impact-header">
              <h4><i class="bi bi-tree indicator-exp-icon"></i> Land Use</h4>
              <p class="indicator-value">{{ formatNumber(Math.abs(totalLandSavings)) }} {{ LAND_UNIT_LABEL_EF31 }} saved</p>
              <p class="indicator-note">Your donations reduce pressure on agricultural land and preserve soil quality.</p>
            </div>

            <div class="indicator-impact-body" v-if="landComparisonsAll">
              <p class="comparison-label">To put this in perspective, this equals the land impact of producing:</p>
              <div class="comparisons-table">
                <div
                  v-for="cmp in landComparisonsAll"
                  :key="'land-'+cmp.key"
                  class="comparison-row"
                >
                  <span class="comparison-icon">{{ cmp.emoji }}</span>
                  <span class="comparison-amount">{{ formatNumber(cmp.equivalentKg) }} kg</span>
                  <span class="comparison-product">{{ cmp.name }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Social Impact Card -->
          <div class="indicator-impact-card">
            <div class="indicator-impact-header">
              <h4><i class="bi bi-people indicator-exp-icon"></i> Social Impact</h4>
              <p class="indicator-value">{{ Math.round(surplusForSelectedPeriod * FWDE) }} kg of food consumed</p>
              <p class="indicator-note">Your donations provide nutrition to those who need it most.</p>
            </div>

            <div class="indicator-impact-body">
              <p>
                Beyond the environmental benefits, your donations make a real difference in people's lives.
                With <strong>{{ Math.round(FWDE * 100) }}%</strong> of your donated food being consumed,
                you're providing <strong>{{ Math.round(surplusForSelectedPeriod * FWDE) }} kg</strong>
                of nutrition {{ periodText }}.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Summary -->
      <div class="summary-section">
        <div class="summary-content">
          <div class="summary-icon">
            <i :class="totalERForSelectedPeriod >= 0 ? 'bi bi-award' : 'bi bi-arrow-up-circle'"></i>
          </div>
          <h3 class="summary-title">
            {{ totalERForSelectedPeriod >= 0 ? 'Keep Up the Great Work!' : 'Small Changes, Big Impact' }}
          </h3>
          <p class="summary-text">
            <span v-if="totalERForSelectedPeriod >= 0">
              Your food donation program is making a positive environmental impact across all key indicators. <span style="text-transform: capitalize">{{ periodText }}</span>
              you're achieving: <strong class="positive">{{ formatNumber(Math.abs(totalERForSelectedPeriod)) }} kg CO₂eq</strong> prevented,
              <strong class="positive">{{ formatNumber(Math.abs(totalWaterSavings)) }} {{ WATER_UNIT_LABEL_EF31 }}</strong> water saved, and
              <strong class="positive">{{ formatNumber(Math.abs(totalLandSavings)) }} {{ LAND_UNIT_LABEL_EF31 }}</strong> land preserved.
              This comprehensive impact demonstrates real commitment to both environmental sustainability and social responsibility.
            </span>
            <span v-else>
              While your donations provide important social benefits and save resources, there's an opportunity to improve
              the carbon footprint. Despite transport adding <strong class="negative">{{ formatNumber(Math.abs(totalERForSelectedPeriod)) }} kg CO₂eq</strong>
              {{ periodText }}, you're still saving <strong class="positive">{{ formatNumber(Math.abs(totalWaterSavings)) }} {{ WATER_UNIT_LABEL_EF31 }}</strong> water and
              <strong class="positive">{{ formatNumber(Math.abs(totalLandSavings)) }} {{ LAND_UNIT_LABEL_EF31 }}</strong> land.
              Optimizing transport logistics (currently <strong>{{ formatDistance(distanceKm) }} km</strong>) could improve your overall sustainability profile.
            </span>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, onMounted, watch, nextTick } from 'vue';
import { useStepperStore } from '@/stores/stepper';
import { useLCAStore } from '@/stores/lca';
import ApexCharts from 'apexcharts';
import { donationEfficiency as FWDE, LAND_UNIT_LABEL_EF31, WATER_UNIT_LABEL_EF31, findBestReference, AGRIBALYSE_REFERENCES } from '@/constants/ef31';
import { calculateReuseHumanMetrics, buildEmptyReuseHumanMetrics } from '@/utils/reuseHumanNE';

export default {
  name: 'ReuseHumanReport',
  setup() {
    const stepperStore = useStepperStore();
    const lcaStore = useLCAStore();

    // Chart refs
    const impactChart = ref(null);
    let impactChartInstance = null;

    // Basic data
    const baseSurplus = computed(() => {
      const value = Number(stepperStore.wasteInfo.amount.value);
      return Number.isFinite(value) ? value : 0;
    });
    const timesFreq = computed(() => Number(stepperStore.wasteInfo.frequency.times) || 1);
    const frequencyInterval = computed(() => stepperStore.wasteInfo.frequency.interval);
    const surplusName = computed(() => stepperStore.wasteInfo.surplusName || 'Unnamed Product');
    const productNotFound = computed(() => lcaStore.productNotFound);

    // Frequency text
    const frequencyText = computed(() => {
      if (frequencyInterval.value === 'once') return '';
      return `(${timesFreq.value}x per ${frequencyInterval.value})`;
    });

    const distanceKm = computed(() => Number(lcaStore.lcaData.transportResult?.distance_km) || 0);
    const transportWeightPerDonation = computed(() => {
      const storedWeight = Number(lcaStore.lcaData.transportResult?.transport_weight_kg);
      if (Number.isFinite(storedWeight) && storedWeight > 0) {
        return storedWeight;
      }
      return baseSurplus.value;
    });

    // Period management
    const selectedPeriod = ref('once');
    const periodOptions = ['once', 'month', 'year'];

    function periodLabel(period) {
      if (period === 'once') return 'One-time donation';
      if (period === 'month') return 'Monthly';
      if (period === 'year') return 'Yearly';
      return period;
    }

    const periodText = computed(() => {
      if (selectedPeriod.value === 'once') return '';
      if (selectedPeriod.value === 'month') return 'per month';
      if (selectedPeriod.value === 'year') return 'per year';
      return '';
    });

    const tablePeriodDescriptor = computed(() => {
      if (selectedPeriod.value === 'once') return 'per donation';
      if (selectedPeriod.value === 'month') return 'per month';
      if (selectedPeriod.value === 'year') return 'per year';
      return '';
    });

    function disablePeriodButton(period) {
      return frequencyInterval.value === 'once' && period !== 'once';
    }

    // Indicator management
    const selectedIndicator = ref('co2');
    const indicatorOptions = [
      { key: 'co2', label: 'CO₂eq', icon: 'bi bi-cloud' },
      { key: 'water', label: 'Water Use', icon: 'bi bi-droplet' },
      { key: 'land', label: 'Land Use', icon: 'bi bi-tree' }
    ];

    const selectedIndicatorName = computed(() => {
      const selected = indicatorOptions.find(opt => opt.key === selectedIndicator.value);
      return selected ? selected.label : '';
    });

    function getSurplusMultiplier(period) {
      const interval = frequencyInterval.value;
      if (interval === 'once') return period === 'once' ? 1 : 0;

      if (interval === 'week') {
        if (period === 'once') return 1;
        if (period === 'month') return timesFreq.value * 4.345;
        if (period === 'year') return timesFreq.value * 52;
      } else if (interval === 'month') {
        if (period === 'once') return 1;
        if (period === 'month') return timesFreq.value;
        if (period === 'year') return timesFreq.value * 12;
      }
      return 1;
    }

    function calcSurplus(period) {
      return baseSurplus.value * getSurplusMultiplier(period);
    }

    function calcTransportWeight(period) {
      const baseWeight = transportWeightPerDonation.value;
      if (!Number.isFinite(baseWeight) || baseWeight <= 0) {
        return 0;
      }
      return baseWeight * getSurplusMultiplier(period);
    }

    // Unified methodology calculations
    const emptyMetrics = buildEmptyReuseHumanMetrics();

    const metricsByPeriod = computed(() => {
      const impacts = lcaStore.lcaData.environmentalImpacts || {};
      const results = {};
      for (const period of periodOptions) {
        results[period] = calculateReuseHumanMetrics({
          impacts,
          massKg: calcSurplus(period),
          distanceKm: distanceKm.value,
          transportMassKg: calcTransportWeight(period),
        });
      }
      return results;
    });

    const metricsForSelectedPeriod = computed(() => metricsByPeriod.value[selectedPeriod.value] || emptyMetrics);
    const co2Indicator = computed(() => metricsForSelectedPeriod.value.indicators?.co2 || emptyMetrics.indicators.co2);
    const waterIndicator = computed(() => metricsForSelectedPeriod.value.indicators?.water || emptyMetrics.indicators.water);
    const landIndicator = computed(() => metricsForSelectedPeriod.value.indicators?.land || emptyMetrics.indicators.land);
    const transportPerKgKm = computed(() => {
      const perTkm = co2Indicator.value.transportPerTkm;
      return typeof perTkm === 'number' ? perTkm / 1000 : 0;
    });

    const metricsForSingleDonation = computed(() => metricsByPeriod.value.once || emptyMetrics);
    const co2IndicatorSingle = computed(() => metricsForSingleDonation.value.indicators?.co2 || emptyMetrics.indicators.co2);
    const waterIndicatorSingle = computed(() => metricsForSingleDonation.value.indicators?.water || emptyMetrics.indicators.water);
    const landIndicatorSingle = computed(() => metricsForSingleDonation.value.indicators?.land || emptyMetrics.indicators.land);

    const palette = {
      production: '#87b140',
      waste: '#a3c955',
      transport: '#c8656d',
      totalPositive: '#6a8e32',
      totalNegative: '#dc3545'
    };

    // Computed values for UI
    const surplusForSelectedPeriod = computed(() => calcSurplus(selectedPeriod.value));
    const productionSavedForSelectedPeriod = computed(() => co2Indicator.value.productionSubstitution);
    const wasteSavedForSelectedPeriod = computed(() => co2Indicator.value.wasteAvoided);
    const transportEmissionsForSelectedPeriod = computed(() => co2Indicator.value.transportAdded);
    const totalERForSelectedPeriod = computed(() => co2Indicator.value.relativeReduction);

    const totalERForSingleDonation = computed(() => co2IndicatorSingle.value.relativeReduction);

    const totalWaterSavings = computed(() => waterIndicator.value.relativeReduction);
    const totalLandSavings = computed(() => landIndicator.value.relativeReduction);

    const totalWaterSavingsSingle = computed(() => waterIndicatorSingle.value.relativeReduction);
    const totalLandSavingsSingle = computed(() => landIndicatorSingle.value.relativeReduction);

    const co2ImpactIsPositiveSingle = computed(() => totalERForSingleDonation.value >= 0);

    const totalSavingsForIndicator = computed(() => {
      if (selectedIndicator.value === 'co2') return totalERForSelectedPeriod.value;
      if (selectedIndicator.value === 'water') return totalWaterSavings.value;
      return totalLandSavings.value;
    });

    const totalLegendDescription = computed(() => (
      totalSavingsForIndicator.value >= 0
        ? 'Shows your overall environmental benefit'
        : 'Highlights where added emissions remain'
    ));

    const totalLegendClass = computed(() => (
      totalSavingsForIndicator.value >= 0 ? 'value-total-positive' : 'value-total-negative'
    ));

    const totalIndicatorStyle = computed(() => ({
      background: totalSavingsForIndicator.value >= 0
        ? palette.totalPositive
        : palette.totalNegative
    }));

    function totalValueClass(indicator) {
      const value = indicator === 'co2'
        ? totalERForSelectedPeriod.value
        : indicator === 'water'
          ? totalWaterSavings.value
          : totalLandSavings.value;
      return value >= 0 ? 'value-total-positive' : 'value-total-negative';
    }

    const waterProductionEffect = computed(() => waterIndicator.value.productionSubstitution);
    const waterWasteSaved = computed(() => waterIndicator.value.wasteAvoided);
    const waterTransportAdded = computed(() => waterIndicator.value.transportAdded);
    const landProductionEffect = computed(() => landIndicator.value.productionSubstitution);
    const landWasteSaved = computed(() => landIndicator.value.wasteAvoided);
    const landTransportAdded = computed(() => landIndicator.value.transportAdded);

    // Persist detailed metrics to the LCA store for verification/debugging
    const storePayload = computed(() => ({
      distanceKm: distanceKm.value,
      productNotFound: productNotFound.value,
      periods: metricsByPeriod.value,
    }));

    watch(storePayload, (payload) => {
      const serialized = JSON.parse(JSON.stringify(payload));
      lcaStore.setReuseHumanCalculations(serialized);
    }, { deep: true, immediate: true });

    // Tangible comparisons using Agribalyse reference data
    const climateComparison = computed(() => {
      if (totalERForSelectedPeriod.value <= 0) return null;
      return findBestReference(totalERForSelectedPeriod.value, 'climate');
    });

    const waterComparison = computed(() => {
      if (totalWaterSavings.value <= 0) return null;
      return findBestReference(totalWaterSavings.value, 'water');
    });

    const landComparison = computed(() => {
      if (totalLandSavings.value <= 0) return null;
      return findBestReference(totalLandSavings.value, 'land');
    });

    // Build comparisons for ALL three reference foods at once (🥔/🍗/🥩)
    function buildAllComparisons(value, indicatorKey) {
      if (typeof value !== 'number' || !isFinite(value) || value <= 0) return null;
      const map = { climate: 'climateImpact', water: 'waterScarcity', land: 'landUse' };
      const prop = map[indicatorKey];
      const keys = ['potato', 'chicken', 'beef'];
      const out = [];
      for (const key of keys) {
        const ref = AGRIBALYSE_REFERENCES[key];
        const refVal = ref && typeof ref[prop] === 'number' ? ref[prop] : null;
        if (ref && refVal && refVal > 0) {
          out.push({
            key,
            emoji: ref.emoji,
            name: ref.name,
            equivalentKg: Math.abs(value) / refVal
          });
        }
      }
      return out.length ? out : null;
    }

    const climateComparisonsAll = computed(() => buildAllComparisons(totalERForSelectedPeriod.value, 'climate'));
    const waterComparisonsAll = computed(() => buildAllComparisons(totalWaterSavings.value, 'water'));
    const landComparisonsAll = computed(() => buildAllComparisons(totalLandSavings.value, 'land'));

    // Format number helper with smart rounding and trimmed zeros
    // - If forceDecimals provided: fixed decimals
    // - Else: abs >= 1000 -> 0 decimals; abs >= 10 -> 1; abs >= 1 -> 1; abs < 1 -> 2
    // - Trailing .0 removed
    function formatNumber(value, forceDecimals = null) {
      if (typeof value !== 'number' || !isFinite(value)) return '0';
      const abs = Math.abs(value);
      const decimals = typeof forceDecimals === 'number'
        ? Math.max(0, forceDecimals)
        : (abs >= 1000 ? 0 : abs >= 1 ? 1 : 2);
      const formatted = value.toFixed(decimals);
      return formatted.replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
    }

    function formatDistance(distance) {
      if (!distance || distance <= 0) return '0 km';
      if (distance < 1) return `${(distance * 1000).toFixed(0)} m`;
      if (distance < 10) return `${distance.toFixed(1)} km`;
      return `${Math.round(distance)} km`;
    }

    // Chart rendering
    function setupImpactChart() {
      if (!impactChart.value) return;

      let data, yAxisTitle, unit;

      if (selectedIndicator.value === 'co2') {
        data = [
          Math.abs(productionSavedForSelectedPeriod.value),
          Math.abs(wasteSavedForSelectedPeriod.value),
          -Math.abs(transportEmissionsForSelectedPeriod.value),
          totalERForSelectedPeriod.value
        ];
        yAxisTitle = 'CO₂eq Savings (kg)';
        unit = ' kg CO₂eq saved';
      } else if (selectedIndicator.value === 'water') {
        data = [
          Math.abs(waterProductionEffect.value),
          Math.abs(waterWasteSaved.value),
          -Math.abs(waterTransportAdded.value),
          totalWaterSavings.value
        ];
        yAxisTitle = 'Water Savings (m3 world eq)';
        unit = ' m3 world eq saved';
      } else {
        data = [
          Math.abs(landProductionEffect.value),
          Math.abs(landWasteSaved.value),
          -Math.abs(landTransportAdded.value),
          totalLandSavings.value
        ];
        yAxisTitle = 'Land Savings (' + LAND_UNIT_LABEL_EF31 + ')';
        unit = ' ' + LAND_UNIT_LABEL_EF31 + ' saved';
      }

      const safe = (v) => (typeof v === 'number' && isFinite(v) ? v : 0);
      const numericData = data.map(safe);

      const totalBarColor = totalSavingsForIndicator.value >= 0
        ? palette.totalPositive
        : palette.totalNegative;

      const chartOptions = {
        series: [{
          name: 'Savings',
          data: numericData
        }],
        chart: {
          type: 'bar',
          height: 400,
          toolbar: { show: false },
          fontFamily: 'inherit',
          animations: {
            enabled: true,
            speed: 400,
            animateGradually: {
              enabled: false
            }
          }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '65%',
            borderRadius: 6,
            distributed: true
          }
        },
        dataLabels: {
          enabled: true,
          formatter: (val) => {
            if (val < 0) return '−' + Math.abs(val).toFixed(1);
            return val.toFixed(1);
          },
          style: {
            fontSize: '12px',
            fontWeight: '500',
            colors: ['#fff']
          },
          offsetY: -25
        },
        colors: [
          palette.production,
          palette.waste,
          palette.transport,
          totalBarColor
        ],
        xaxis: {
          categories: ['Production\nAvoided', 'Waste\nSaved', 'Transport\nOffset', 'Total\nSavings'],
          labels: {
            style: {
              fontSize: '12px',
              colors: '#555',
              fontWeight: '500'
            }
          }
        },
        yaxis: {
          title: {
            text: yAxisTitle,
            style: {
              fontSize: '13px',
              fontWeight: '600',
              color: '#444'
            }
          },
          labels: {
            formatter: (val) => {
              if (val < 0) return '−' + Math.abs(val).toFixed(0);
              return val.toFixed(0);
            },
            style: {
              fontSize: '11px',
              colors: '#666'
            }
          },
          min: (() => {
            const minY = Math.min(0, ...numericData);
            return minY < 0 ? minY * 1.1 : 0;
          })()
        },
        grid: {
          borderColor: '#f0f0f0',
          strokeDashArray: 0,
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } },
          padding: {
            top: 10,
            right: 10,
            bottom: 10,
            left: 10
          }
        },
        tooltip: {
          y: {
            formatter: (val) => {
              if (val < 0) return 'Offset: ' + formatNumber(Math.abs(val)) + unit.replace(' saved', '');
              return formatNumber(val) + unit;
            }
          }
        },
        legend: {
          show: false
        }
      };

      if (impactChartInstance) {
        impactChartInstance.updateOptions(chartOptions);
      } else {
        impactChartInstance = new ApexCharts(impactChart.value, chartOptions);
        impactChartInstance.render();
      }
    }

    // Lifecycle
    onMounted(async () => {
      await nextTick();
      setupImpactChart();
    });

    // Update chart when user changes period/indicator, or when underlying data arrives/changes
    watch([
      selectedPeriod,
      selectedIndicator,
      surplusForSelectedPeriod,
      productionSavedForSelectedPeriod,
      wasteSavedForSelectedPeriod,
      transportEmissionsForSelectedPeriod,
      totalERForSelectedPeriod,
      waterProductionEffect,
      waterWasteSaved,
      waterTransportAdded,
      landProductionEffect,
      landWasteSaved,
      landTransportAdded,
      () => lcaStore.lcaData.environmentalImpacts,
      () => lcaStore.lcaData.transportResult?.distance_km,
      () => lcaStore.lcaData.transportResult?.transport_weight_kg
    ], async () => {
      await nextTick();
      setupImpactChart();
    }, { deep: true });

    return {
      stepperStore,
      surplusName,
      baseSurplus,
      frequencyText,
      distanceKm,
      transportWeightPerDonation,
      selectedPeriod,
      periodOptions,
      periodLabel,
      periodText,
      disablePeriodButton,
      selectedIndicator,
      indicatorOptions,
      selectedIndicatorName,
      surplusForSelectedPeriod,
      productionSavedForSelectedPeriod,
      wasteSavedForSelectedPeriod,
      transportEmissionsForSelectedPeriod,
      totalERForSelectedPeriod,
      totalWaterSavings,
      totalLandSavings,
      totalERForSingleDonation,
      totalWaterSavingsSingle,
      totalLandSavingsSingle,
      co2ImpactIsPositiveSingle,
      waterProductionEffect,
      waterWasteSaved,
      waterTransportAdded,
      landProductionEffect,
      landWasteSaved,
      landTransportAdded,
      transportPerKgKm,
      LAND_UNIT_LABEL_EF31,
      WATER_UNIT_LABEL_EF31,
      FWDE,
      formatNumber,
      formatDistance,
      impactChart,
      tablePeriodDescriptor,
      palette,
      totalLegendDescription,
      totalLegendClass,
      totalIndicatorStyle,
      totalValueClass,
      climateComparison,
      waterComparison,
      landComparison,
      climateComparisonsAll,
      waterComparisonsAll,
      landComparisonsAll
    };
  }
};
</script>

<style scoped>
/* Reset and Base */
.reuse-human-report {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  color: #333;
  line-height: 1.6;
}

.report-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
}

/* Header */
.report-header {
  text-align: center;
  margin-bottom: 3rem;
  padding-bottom: 2rem;
  border-bottom: 2px solid #f0f0f0;
}

.report-title {
  font-size: 2rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 0.5rem;
}

.report-subtitle {
  font-size: 1.1rem;
  color: #666;
  margin: 0;
}

/* Metrics Section */
.metrics-section {
  margin-bottom: 2rem;
}

/* Understanding Environmental Indicators Section */
.indicators-explanation {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
}

.indicator-explanation-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1.5rem;
  transition: box-shadow 0.2s, transform 0.2s;
}

.indicator-explanation-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.indicator-exp-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.indicator-exp-icon {
  font-size: 1.5rem;
  color: #87b140;
}

.indicator-exp-header h4 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
}

.indicator-exp-subtitle {
  font-size: 0.9rem;
  color: #87b140;
  font-weight: 600;
  margin: 0 0 0.5rem;
  font-style: italic;
}

.indicator-explanation-card p:last-child {
  margin: 0;
  color: #555;
  line-height: 1.6;
  font-size: 0.95rem;
}

.metrics-title {
  text-align: center;
  margin: 0 0 1.5rem 0;
  font-size: 1.1rem;
  color: #666;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.metric-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1.5rem;
  transition: box-shadow 0.2s;
  position: relative;
}

.metric-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.metric-icon {
  position: absolute;
  top: 1rem;
  right: 1rem;
  font-size: 1.5rem;
  color: #d0d0d0;
  opacity: 0.5;
}

.metric-label {
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.no-transform {
  text-transform: none !important;
  display: inline;
}

.metric-value {
  font-size: 1.75rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.25rem;
  line-height: 1.2;
}

.metric-value.positive {
  color: #87B140;
}

.metric-value.negative {
  color: #dc3545;
}

.metric-detail {
  font-size: 0.9rem;
  color: #888;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Content Sections */
.content-section {
  margin-bottom: 3rem;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #f0f0f0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.section-title i {
  font-size: 1.3rem;
  color: #87b140;
}

/* Methodology Section */
.methodology-content {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.methodology-explanation h4 {
  color: #2c3e50;
  font-size: 1.2rem;
  margin: 1.5rem 0 1rem;
}

.methodology-explanation p {
  color: #555;
  line-height: 1.8;
  margin-bottom: 1rem;
}

.impact-explanation {
  margin: 2rem 0;
}

.impact-item {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 3px solid #87b140;
}

.impact-number {
  width: 40px;
  height: 40px;
  background: #87b140;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.impact-text h5 {
  color: #2c3e50;
  font-size: 1.1rem;
  margin: 0 0 0.5rem;
}

.impact-text p {
  margin: 0;
  color: #555;
  line-height: 1.6;
}

.methodology-summary {
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 2px solid #f0f0f0;
}

.calculation-note {
  margin-top: 1.5rem;
  padding: 1rem;
  background: #fff8e1;
  border: 1px solid #ffc107;
  border-radius: 6px;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.calculation-note i {
  color: #ff9800;
  font-size: 1.2rem;
  flex-shrink: 0;
  margin-top: 0.2rem;
}

.calculation-note p {
  margin: 0;
  font-size: 0.9rem;
  color: #555;
  flex: 1;
}

/* Impact Breakdown Section */
.impact-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.period-selector-inline {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.period-label {
  font-size: 0.9rem;
  color: #666;
  font-weight: 500;
}

.period-buttons {
  display: flex;
  gap: 0.5rem;
}

.selector-btn {
  padding: 0.4rem 1rem;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
}

.selector-btn:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #87b140;
}

.selector-btn.active {
  background: #87b140;
  color: white;
  border-color: #87b140;
}

.selector-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.seasonal-note {
  padding: 0.75rem;
  background: #fff3cd;
  border-radius: 6px;
  color: #856404;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.seasonal-note i {
  font-size: 1rem;
}

/* Unified Impact Card */
.unified-impact-card {
  background: white;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

/* Card Header with Tabs */
.card-header-tabs {
  background: #f8f9fa;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.card-main-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
}

.indicator-tabs {
  display: flex;
  gap: 0.25rem;
  background: white;
  padding: 0.25rem;
  border-radius: 6px;
  border: 1px solid #dee2e6;
}

.indicator-tab {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  background: transparent;
  border: none;
  border-radius: 4px;
  font-size: 0.875rem;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.indicator-tab:hover {
  background: #f8f9fa;
  color: #87b140;
}

.indicator-tab.active {
  background: #87b140;
  color: white;
}

.indicator-tab i {
  font-size: 0.95rem;
}

/* Card Body Grid */
.card-body-grid {
  display: grid;
  grid-template-columns: 380px 1px 1fr;
  min-height: 450px;
}

/* Left Panel - Understanding */
.understanding-panel {
  padding: 2rem;
  display: flex;
  flex-direction: column;
}

.panel-title {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 0.5rem;
}

.panel-intro {
  font-size: 0.875rem;
  color: #666;
  margin: 0 0 1.5rem;
  line-height: 1.5;
}

.savings-breakdown {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
}

.breakdown-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.breakdown-item.total {
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
  margin-top: 0.5rem;
}

.breakdown-indicator {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  flex-shrink: 0;
  margin-top: 2px;
}

.breakdown-indicator.green-high {
  background: #87b140;
}

.breakdown-indicator.gray {
  background: #d4d4d4;
}

.breakdown-indicator.red {
  background: #c8656d;
}

.breakdown-indicator.green-total {
  background: linear-gradient(135deg, #6a8e32, #87b140);
}

.breakdown-info {
  flex: 1;
}

.breakdown-info strong {
  display: block;
  font-size: 0.9rem;
  color: #2c3e50;
  margin-bottom: 0.2rem;
}

.breakdown-info span {
  display: block;
  font-size: 0.8rem;
  color: #6c757d;
  line-height: 1.3;
}

.panel-note {
  margin-top: 1.5rem;
  padding: 0.75rem;
  background: #f0f8f0;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid #d4e8d4;
}

.panel-note i {
  color: #87b140;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.panel-note span {
  font-size: 0.825rem;
  color: #555;
  line-height: 1.4;
}

/* Vertical Divider */
.vertical-divider {
  background: #e9ecef;
  width: 1px;
}

/* Right Panel - Chart */
.chart-panel {
  padding: 2rem;
  display: flex;
  flex-direction: column;
}

.chart-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 1.5rem;
}

.chart-title {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
}

.chart-indicator {
  font-size: 0.875rem;
  color: #6c757d;
  background: #f8f9fa;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
}

.chart-area-unified {
  width: 100%;
  min-height: 380px;
  flex: 1;
}

/* Data Table */
.data-table-container {
  overflow-x: auto;
  background: white;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  position: relative;
}

.table-period-header {
  padding: 1rem;
  background: linear-gradient(to right, #f8f9fa, #ffffff);
  border-bottom: 2px solid #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
}

.period-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: white;
  border: 2px solid #87b140;
  border-radius: 20px;
  color: #2c3e50;
  font-weight: 600;
  font-size: 0.9rem;
}

.period-badge i {
  color: #87b140;
  font-size: 1rem;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th {
  background: #f8f9fa;
  padding: 1rem;
  font-weight: 600;
  font-size: 0.875rem;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e0e0e0;
  vertical-align: middle;
}

.data-table th:first-child {
  text-align: left;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.header-content i {
  font-size: 1.2rem;
  opacity: 0.7;
}

.header-content > div {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.unit-label {
  font-size: 0.75rem;
  color: #999;
  font-weight: 400;
  text-transform: none;
  margin-top: 0.25rem;
}

.data-table td {
  padding: 1rem;
  border-bottom: 1px solid #f0f0f0;
  font-size: 0.95rem;
  color: #333;
}

.data-table tbody tr:hover {
  background: #f8f9fa;
}

.total-row {
  background: #f8f9fa;
  font-weight: 600;
}

.total-row td {
  border-top: 2px solid #e0e0e0;
  border-bottom: none;
}

.category-label {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.category-name {
  font-weight: 500;
  color: #2c3e50;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.category-name i {
  font-size: 0.9rem;
}

.category-desc {
  font-size: 0.85rem;
  color: #888;
  margin-left: 1.4rem;
}

.text-right {
  text-align: right;
}

.text-center {
  text-align: center;
}

.table-value {
  font-weight: 600;
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.5);
}

.value-production {
  color: #87b140;
}

.value-waste {
  color: #a3c955;
}

.value-transport {
  color: #c8656d;
}

.value-total-positive {
  color: #6a8e32;
}

.value-total-negative {
  color: #dc3545;
}

/* Insights */

/* Indicators Impact Grid */
.indicators-impact-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.indicator-impact-card {
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  overflow: hidden;
}

.indicator-impact-header {
  padding: 1rem;
  background: #fafafa;
  border-bottom: 1px solid #e9ecef;
}

.indicator-impact-header h4 {
  margin: 0 0 0.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: #333;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.indicator-value {
  margin: 0.5rem 0 0.25rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: #87b140;
  line-height: 1.3;
}

.indicator-unit {
  margin: 0.25rem 0;
  font-size: 0.8rem;
  color: #777;
  font-style: italic;
  line-height: 1.2;
}

.indicator-note {
  margin: 0.5rem 0 0;
  font-size: 0.85rem;
  color: #666;
  line-height: 1.4;
}

.indicator-impact-body {
  padding: 1rem;
}

.comparison-label {
  font-size: 0.85rem;
  font-weight: 500;
  color: #666;
  margin-bottom: 0.5rem;
  display: block;
}

.comparisons-table {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.comparison-row {
  display: grid;
  grid-template-columns: 32px 1fr auto;
  gap: 0.75rem;
  align-items: center;
  padding: 0.5rem;
  background: #fafafa;
  border-radius: 3px;
  font-size: 0.9rem;
}

.comparison-icon {
  font-size: 1.2rem;
  text-align: center;
}

.comparison-amount {
  font-weight: 600;
  color: #333;
}

.comparison-product {
  color: #666;
  text-align: right;
}

/* Regular Insights Grid (for other cards) */
.insights-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
}

.insight-card {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.insight-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.insight-title i {
  font-size: 1rem;
  color: #87b140;
}

.insight-card p {
  margin: 0;
  color: #555;
  line-height: 1.6;
}

/* Summary */
.summary-section {
  margin-top: 3rem;
  padding: 2rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 8px;
  border: 2px solid #87b140;
}

.summary-content {
  text-align: center;
}

.summary-icon {
  font-size: 3rem;
  color: #87b140;
  margin-bottom: 1rem;
}

.summary-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 1rem;
}

.summary-text {
  font-size: 1.05rem;
  color: #555;
  line-height: 1.8;
  max-width: 800px;
  margin: 0 auto;
}

.positive {
  color: #6a8e32;
}

.negative {
  color: #dc3545;
}

/* Responsive Design */
@media (max-width: 768px) {
  .report-container {
    padding: 1rem;
  }

  .report-title {
    font-size: 1.5rem;
  }

  .metrics-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .metric-value {
    font-size: 1.5rem;
  }

  .impact-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .period-selector-inline {
    width: 100%;
  }

  .period-buttons {
    flex-wrap: wrap;
    width: 100%;
  }

  .selector-btn {
    flex: 1;
    min-width: 80px;
  }

  /* Unified Card Responsive */
  .card-header-tabs {
    flex-direction: column;
    align-items: flex-start;
  }

  .indicator-tabs {
    width: 100%;
  }

  .indicator-tab {
    flex: 1;
    font-size: 0.8rem;
    padding: 0.4rem 0.5rem;
  }

  .indicator-tab span {
    display: none;
  }

  .indicator-tab i {
    font-size: 1.1rem;
  }

  .card-body-grid {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1px auto;
  }

  .vertical-divider {
    width: 100%;
    height: 1px;
  }

  .understanding-panel,
  .chart-panel {
    padding: 1.5rem;
  }

  .impact-item {
    flex-direction: column;
    text-align: center;
  }

  .impact-number {
    margin: 0 auto;
  }

  .section-title {
    font-size: 1.25rem;
  }

  .section-title i {
    font-size: 1.1rem;
  }

  .data-table {
    font-size: 0.875rem;
  }

  .data-table th,
  .data-table td {
    padding: 0.75rem 0.5rem;
  }

  .table-period-header {
    padding: 0.75rem;
  }

  .period-badge {
    font-size: 0.85rem;
    padding: 0.4rem 0.8rem;
  }

  .header-content {
    flex-direction: column;
  }

  .header-content i {
    margin-right: 0;
  }

  .unit-label {
    font-size: 0.65rem;
  }

  .insights-grid {
    grid-template-columns: 1fr;
  }

  .indicators-impact-grid {
    grid-template-columns: 1fr;
  }

  .indicator-impact-header {
    padding: 0.875rem;
  }

  .indicator-impact-header h4 {
    font-size: 0.9rem;
  }

  .indicator-value {
    font-size: 1rem;
  }

  .indicator-note {
    font-size: 0.8rem;
  }

  .comparison-row {
    grid-template-columns: 28px 1fr auto;
    gap: 0.5rem;
    padding: 0.4rem;
    font-size: 0.85rem;
  }

  .comparison-icon {
    font-size: 1.1rem;
  }

  .comparison-product {
    font-size: 0.8rem;
  }

  .chart-legend {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .report-title {
    font-size: 1.25rem;
  }

  .metric-value {
    font-size: 1.25rem;
  }

  .metric-icon {
    font-size: 1.2rem;
  }

  .data-table {
    font-size: 0.8rem;
  }

  .category-desc {
    display: none;
  }

  .selector-btn,
  .indicator-btn {
    font-size: 0.85rem;
    padding: 0.4rem 0.8rem;
  }

  .summary-icon {
    font-size: 2rem;
  }

  .indicators-impact-grid {
    gap: 0.75rem;
  }

  .indicator-impact-card {
    border-radius: 4px;
  }

  .indicator-impact-header {
    padding: 0.75rem;
  }

  .indicator-impact-header h4 {
    font-size: 0.85rem;
  }

  .indicator-impact-body {
    padding: 0.75rem;
  }

  .indicator-value {
    font-size: 0.95rem;
  }

  .indicator-unit {
    font-size: 0.75rem;
  }

  .indicator-note {
    font-size: 0.8rem;
  }

  .comparison-row {
    grid-template-columns: 24px 1fr auto;
    gap: 0.4rem;
    padding: 0.4rem;
    font-size: 0.8rem;
  }

  .comparison-icon {
    font-size: 1rem;
  }

  .comparison-amount {
    font-size: 0.8rem;
  }

  .comparison-product {
    font-size: 0.75rem;
  }
}

/* Print Styles */
@media print {
  .period-buttons,
  .indicator-buttons {
    display: none;
  }

  .metric-card,
  .insight-card,
  .indicator-impact-card {
    break-inside: avoid;
  }

  .chart-container {
    break-inside: avoid;
  }
}
</style>
